/**
 * Shared default-specifications map for asset-model / asset-component-model
 * Specifications sub-forms.
 *
 * The seven WIND_TURBINE component groups (TOWER / FOUNDATION / NACELLE
 * / SPINNER / ROTOR_BLADE / ACCESSORIES / UNDEFINED) are identical
 * across the two surfaces (Story 059's turbine-models and Story 062's
 * component-models). Story 062 brings the second consumer — rule of
 * two satisfied — so the map graduates to `packages/ui/src/data/`.
 *
 * The keys here are `group_code` strings as resolved against
 * `public.global_component_groups.group_code`. The Story 062 Add /
 * Edit modal renders the Component Group Autocomplete with the
 * group-id as the form value but keys this map on the `groupCode`
 * lookup.
 *
 * Defaults are UX-locked (Story 059 §Specifications-parameter map,
 * verbatim) — see `docs/ux/059-turbine-models-management.md` §9.3 and
 * `docs/ux/062-component-models-design-brief.md` §4.
 */

export interface DefaultSpecificationRow {
  parameter: string;
  unit: string;
  value: string;
}

const GROUP_CODE_DEFAULTS: Readonly<
  Record<string, readonly DefaultSpecificationRow[]>
> = {
  ROTOR_BLADE: [
    { parameter: "length", unit: "meter", value: "" },
    { parameter: "weight", unit: "kilogram", value: "" },
    { parameter: "surface_finish", unit: "text", value: "" },
  ],
  TOWER: [
    { parameter: "height", unit: "meter", value: "" },
    { parameter: "diameter_top", unit: "meter", value: "" },
    { parameter: "diameter_bottom", unit: "meter", value: "" },
    { parameter: "material", unit: "text", value: "" },
  ],
  NACELLE: [
    { parameter: "weight", unit: "kilogram", value: "" },
    { parameter: "cooling_type", unit: "text", value: "" },
  ],
  FOUNDATION: [
    { parameter: "type", unit: "text", value: "" },
    { parameter: "depth", unit: "meter", value: "" },
  ],
  SPINNER: [
    { parameter: "diameter", unit: "meter", value: "" },
    { parameter: "material", unit: "text", value: "" },
  ],
  ACCESSORIES: [],
  UNDEFINED: [],
};

/**
 * Return the default parameter rows for a given component-group code.
 * Unknown group code → empty array (no defaults).
 */
export function defaultsForGroupCode(
  groupCode: string | null | undefined,
): readonly DefaultSpecificationRow[] {
  if (!groupCode) return [];
  return GROUP_CODE_DEFAULTS[groupCode] ?? [];
}
